/**
 * Extract polar wall segments from a RadialMazeState for collision detection.
 *
 * Converts the cell wall flags into ArcWall and RadialWall objects
 * that can be tested with `isPathCrossingRadialWall` from polarUtils.
 */

import type { RadialMazeState } from '../types/maze-types';
import type { PolarWall } from './polarUtils';

const TWO_PI = 2 * Math.PI;

/**
 * Build an array of PolarWall objects from the radial maze cell structure.
 * Each wall is generated once — duplicates are avoided by only emitting
 * outward arcs and clockwise spokes (matching the renderer logic).
 */
export function extractPolarWalls(state: RadialMazeState): PolarWall[] {
  const { center, ringWidth, cells, sectorsPerRing, rings } = state;
  const walls: PolarWall[] = [];

  for (let r = 0; r < rings; r++) {
    const sectorCount = sectorsPerRing[r];
    const sectorAngle = TWO_PI / sectorCount;
    const innerRadius = r * ringWidth;
    const outerRadius = (r + 1) * ringWidth;

    for (let s = 0; s < sectorCount; s++) {
      const cell = cells[r][s];
      const startAngle = s * sectorAngle;
      const endAngle = (s + 1) * sectorAngle;

      // Outward wall — arc at outerRadius
      if (cell.walls.outward) {
        walls.push({
          type: 'arc',
          center,
          radius: outerRadius,
          startAngle,
          endAngle,
        });
      }

      // Inward wall — only when sector counts differ (to avoid duplicates with outward)
      if (r > 0 && cell.walls.inward) {
        const innerSectorCount = sectorsPerRing[r - 1];
        if (innerSectorCount !== sectorCount) {
          walls.push({
            type: 'arc',
            center,
            radius: innerRadius,
            startAngle,
            endAngle,
          });
        }
      }

      // Clockwise radial wall — spoke at endAngle
      if (cell.walls.cw) {
        walls.push({
          type: 'radial',
          center,
          angle: endAngle,
          innerRadius,
          outerRadius,
        });
      }
    }
  }

  return walls;
}
